import { tableAnatomy } from '@chakra-ui/react/anatomy';
import { defineSlotRecipe } from '@chakra-ui/react/styled-system';

import { rem } from '../themeUtils';

// A control like a checkbox renders as an inline-flex <label>, which sits on the line box's
// baseline — so the text strut's descender space leaves it a pixel or two above the row's centre.
// Making it block-level takes the line box out of the equation and lets vertical-align: middle
// centre it exactly.
const controlOnlyCell = {
  '& > :where(label):only-child': {
    display: 'flex',
  },
};

// A tinted row means "selected", and only a Checkbox or a Radio marks it — plus `data-selected` on
// the row itself, the explicit opt-in for a table that tints on its own terms (a switched-on row,
// say). Everything else is left alone, which is the narrowing over a bare `input:checked`: a switch
// renders a hidden input[type="checkbox"] too, so a toggle in a data cell read as a selected row.
//
// `[data-part="item"]` is the load-bearing half of the radio branch. Ark's SegmentGroup is Zag's
// radio-group anatomy renamed, so a segment sits under its own scope — but scope alone would still
// match `RadioGroup.Root`, which wraps the whole table in the `Selectable` pattern, and with it every
// checked radio inside. Requiring `item` on the same element pins the match to a single radio.
//
// Known gap: BitkitSelectableTag is a `Checkbox.Root` underneath, so one in a data cell tints its
// row. It has no consumers today; when it gets one, put `data-selectable-tag` on its `Checkbox.Root`
// and `:not([data-selectable-tag])` on the checkbox branch here.
const selectedRow =
  '&:has([data-scope="checkbox"] input:checked), &:has([data-scope="radio-group"][data-part="item"] input:checked), &[data-selected]';

const tableSlotRecipe = defineSlotRecipe({
  className: 'table',
  slots: [
    ...tableAnatomy.keys(),
    'actionsCell',
    'actionsGroup',
    'bulkActionsGroup',
    'bulkActionsButtons',
    'definitionTrigger',
    'expandCell',
    'expandTrigger',
    'expandableRow',
    'expandedCell',
    'expandedContent',
    'expandedRow',
    'filterControls',
    'linkCell',
    'linkedRow',
    'sortColumnHeader',
    'sortTrigger',
    'utilityCell',
  ],
  base: {
    root: {
      fontVariantNumeric: 'lining-nums tabular-nums',
      textAlign: 'start',
      width: 'full',
    },
    caption: {
      captionSide: 'top !important',
      textAlign: 'start',
      '& > * + *': {
        marginBlockStart: '4',
      },
    },
    cell: {
      textStyle: 'body/md/regular',
    },
    actionsCell: {
      textAlign: 'end',
      whiteSpace: 'nowrap',
      // Shrink-to-fit: a table cell can't go below its content width, so any value under the
      // content's own width collapses the column around the buttons and leaves the rest to
      // the data columns.
      width: '1%',
    },
    actionsGroup: {
      alignItems: 'center',
      display: 'inline-flex',
      gap: '8',
      position: 'relative',
    },
    bulkActionsGroup: {
      alignItems: 'center',
      display: 'flex',
      gap: '12',
    },
    bulkActionsButtons: {
      alignItems: 'center',
      display: 'flex',
      gap: '12',
      marginInlineStart: 'auto',
    },
    // The column header's own `heading/h5` has to survive, so the trigger inherits its type and
    // colour rather than setting them — which is what rules out BitkitDefinitionTooltip in a header,
    // since its recipe always pins a textStyle. The offset is that recipe's own value for 14px type.
    definitionTrigger: {
      color: 'inherit',
      cursor: 'pointer',
      font: 'inherit',
      letterSpacing: 'inherit',
      maxWidth: 'full',
      textDecoration: 'underline dotted',
      textDecorationColor: 'color/neutral/bold',
      textUnderlineOffset: '0.25em',
      _hover: {
        textDecorationColor: 'color/purple/base',
      },
    },
    expandCell: {
      paddingBlock: 0,
      paddingInline: 0,
      verticalAlign: 'middle',
      width: '48',
    },
    // A fixed 32x32 box (per the design), centred in the cell by the cell's own vertical-align.
    // Deriving the size from padding instead leaves it at the mercy of the Collapsible recipe's
    // asymmetric paddingBlockStart, which is what pushed the chevron 2px above the row's centre.
    expandTrigger: {
      alignItems: 'center',
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      display: 'flex',
      height: '32',
      justifyContent: 'center',
      marginInlineStart: '12',
      padding: 0,
      width: '32',
    },
    expandableRow: {
      '& > td': {
        borderBottomWidth: 0,
      },
    },
    expandedCell: {
      height: 0,
      paddingBlock: 0,
      paddingInline: 0,
    },
    expandedContent: {
      borderBlockStartColor: 'border/minimal',
      borderBlockStartStyle: 'solid',
      borderBlockStartWidth: '1',
      marginInlineEnd: '16',
      marginInlineStart: '48',
      paddingBlockEnd: '24',
      paddingBlockStart: '16',
      paddingInlineEnd: '16',
      paddingInlineStart: '12',
    },
    expandedRow: {
      _hover: {
        background: 'none',
      },
    },
    filterControls: {
      alignItems: 'center',
      display: 'flex',
      flexWrap: 'wrap',
      gap: '16',
      width: 'full',
    },
    linkCell: {
      // The link inside is the row's link: its stretched pseudo covers the whole row, so the row
      // becomes the click and focus target.
      '& a': {
        textDecoration: 'none',
        // The row's own background change is the hover affordance; the link recipe's hover
        // underline would fire on every row hover, since the stretched pseudo covers the row.
        _hover: {
          textDecoration: 'none',
        },
        _after: {
          content: '""',
          inset: 0,
          position: 'absolute',
        },
        _focusVisible: {
          outline: 'none',
          _after: {
            outline: 'var(--focus-ring-width) var(--focus-ring-style) var(--focus-ring-color)',
            outlineOffset: 'calc(var(--focus-ring-width) * -1)',
          },
        },
      },
    },
    linkedRow: {
      position: 'relative',
      _hover: {
        backgroundColor: 'background/secondary',
      },
      '& :where(a:not([data-link-cell] a), button, input, label, select)': {
        position: 'relative',
        zIndex: '1',
      },
    },
    sortColumnHeader: {
      paddingBlock: 0,
      paddingInline: 0,
    },
    sortTrigger: {
      alignItems: 'center',
      appearance: 'none',
      background: 'none',
      border: 'none',
      color: 'inherit',
      cursor: 'pointer',
      display: 'flex',
      font: 'inherit',
      gap: '8',
      height: 'full',
      paddingBlock: '12',
      paddingInline: '16',
      userSelect: 'none',
      width: 'full',
      _focusVisible: {
        outlineOffset: '-3px',
      },
      _hover: {
        backgroundColor: 'background/active',
      },
    },
    utilityCell: {
      height: 'auto',
      paddingBlock: '12',
      paddingInline: '16',
    },
  },
  variants: {
    variant: {
      default: {
        root: {
          borderColor: 'border/regular',
          borderRadius: '4',
          borderStyle: 'solid',
          borderWidth: '1',
          overflow: 'hidden',
        },
        header: {
          '& :where(th)': {
            backgroundColor: 'background/tertiary',
          },
        },
        columnHeader: {
          borderBottomColor: 'border/minimal',
          paddingBlock: '12',
          paddingInline: '16',
        },
        cell: {
          borderBottomColor: 'border/minimal',
          borderTopColor: 'border/minimal',
          paddingInline: '16',
        },
        footer: {
          borderTopColor: 'border/minimal',
        },
      },
      borderless: {
        columnHeader: {
          borderBottomColor: 'border/regular',
          padding: '12',
        },
        cell: {
          borderBottomColor: 'border/regular',
          borderTopColor: 'border/regular',
          paddingInline: '12',
        },
        footer: {
          borderTopColor: 'border/regular',
        },
      },
      card: {
        columnHeader: {
          borderBottomColor: 'border/minimal',
          height: '48',
          padding: '12',
        },
        cell: {
          borderBottomColor: 'border/minimal',
          borderTopColor: 'border/minimal',
          paddingInline: '12',
        },
        row: {
          'thead &, tbody &': {
            '& > :where(td, th):first-child': {
              paddingInlineStart: '24',
            },
            '& > :where(td, th):last-child': {
              paddingInlineEnd: '24',
            },
          },
        },
        footer: {
          borderTopColor: 'border/minimal',
        },
      },
    },
    size: {
      sm: {
        cell: {
          height: '48',
        },
      },
      md: {
        cell: {
          height: rem(56),
        },
      },
      lg: {
        cell: {
          height: '64',
        },
      },
    },
    // Hovering a row tints it. Usually a reading aid on a long or wide table rather than a hint
    // that the row is clickable, so it says nothing about the row being interactive — turn it off
    // for short, static tables where there is no place to lose.
    rowHover: {
      true: {},
      false: {},
    },
    layout: {
      table: {
        root: {
          borderCollapse: 'separate',
          borderSpacing: '0',
        },
        caption: {
          marginBlockEnd: '24',
        },
        columnHeader: {
          borderBottomStyle: 'solid',
          borderBottomWidth: '1',
          color: 'text/primary',
          fontWeight: 'bold',
          textAlign: 'start',
          textStyle: 'heading/h5',
          verticalAlign: 'middle',
          ...controlOnlyCell,
        },
        cell: {
          borderBottomStyle: 'solid',
          borderBottomWidth: '1',
          verticalAlign: 'middle',
          ...controlOnlyCell,
          // border-collapse: separate ignores borders on tfoot itself (CSS 2.1 §17.6.1),
          // so the body/footer divider has to live on the footer's cells.
          // A footer holds one full-width block (pagination, a "new item" action) that brings its
          // own padding and height, so the body cell's 16px inset and fixed row height would
          // double up on it.
          'tfoot &': {
            borderBottomWidth: 0,
            borderTopStyle: 'solid',
            borderTopWidth: '1',
            height: 'auto',
            paddingBlock: 0,
            paddingInline: 0,
          },
        },
        body: {
          '& :where(tr)': {
            [selectedRow]: {
              backgroundColor: 'background/selected',
              _hover: {
                backgroundColor: 'background/selected',
              },
            },
          },
          '& :where(tr:last-child) td': {
            borderBottomWidth: 0,
          },
        },
      },
      stacked: {
        root: {
          display: 'block',
        },
        caption: {
          display: 'block',
          padding: '16',
          backgroundColor: 'background/secondary',
          borderBottomColor: 'border/minimal',
          borderBottomStyle: 'solid',
          borderBottomWidth: '1',
        },
        header: {
          borderWidth: 0,
          clip: 'rect(0, 0, 0, 0)',
          height: '1',
          margin: '-1px',
          overflow: 'hidden',
          padding: 0,
          position: 'absolute',
          whiteSpace: 'nowrap',
          width: '1',
        },
        body: {
          display: 'block',
        },
        row: {
          borderBottomColor: 'border/minimal',
          borderBottomStyle: 'solid',
          borderBottomWidth: '1',
          display: 'block',
          paddingBlock: '12',
          '&:last-child': {
            borderBottomWidth: 0,
          },
        },
        cell: {
          alignItems: 'center',
          display: 'flex',
          minHeight: '48',
          paddingInline: '16',
          _before: {
            alignSelf: 'center',
            color: 'text/primary',
            content: 'attr(data-label)',
            flexShrink: 0,
            fontWeight: 'semibold',
            marginInlineEnd: '16',
            width: '96',
          },
          'tfoot &': {
            display: 'block',
            paddingInline: 0,
            _before: {
              content: 'none',
            },
          },
        },
        // display: block takes tfoot out of the table model, so a border on it does render here
        footer: {
          borderTopStyle: 'solid',
          borderTopWidth: '1',
          display: 'block',
        },
      },
    },
  },
  compoundVariants: [
    {
      css: {
        row: {
          borderBottomColor: 'border/regular',
        },
      },
      layout: 'stacked',
      variant: 'borderless',
    },
    // The card gutter and the 12px cell padding are for the wide layout. Stacked rows are blocks
    // whose cells run vertically, so an inline indent on only the first and last one reads as a
    // misalignment — everything goes back to the stacked layout's own 16px.
    {
      css: {
        cell: {
          paddingInline: '16',
        },
        row: {
          'thead &, tbody &': {
            '& > :where(td, th):first-child': {
              paddingInlineStart: '16',
            },
            '& > :where(td, th):last-child': {
              paddingInlineEnd: '16',
            },
          },
        },
      },
      layout: 'stacked',
      variant: 'card',
    },
    // The `size` variant pins a fixed cell height (48/56/64px), and it is merged after `layout`
    // regardless of declaration order — so only a compound variant can release it. Stacked cells
    // are flex containers whose content (label gutter + a multi-line value) routinely needs more
    // room than the fixed height, and the stacked `cell` keeps `minHeight: 48` as the floor.
    {
      css: {
        cell: {
          height: 'auto',
        },
      },
      layout: 'stacked',
    },
    // Scoped to `layout: table` because that is where the hover lived before: the stacked layout
    // renders rows as blocks and never tinted them.
    {
      css: {
        body: {
          '& :where(tr)': {
            _hover: {
              backgroundColor: 'background/secondary',
            },
          },
        },
      },
      layout: 'table',
      rowHover: true,
    },
  ],
  defaultVariants: {
    layout: 'table',
    rowHover: true,
    size: 'lg',
    variant: 'default',
  },
});

export default tableSlotRecipe;
